import React, { useState } from 'react';
import BaseModal from 'src/components/BaseModal';
import Button from 'src/components/Button';
import useModal from 'src/hooks/useModal';
import { useAlerts } from 'src/common/AlertManager';
import AddonEventHooksForm from 'src/components/AddonEventHooksForm';
import styles from './styles.module.scss';

const ADDON_DISPLAY_NAMES: Record<string, string> = {
  FACE_RECOGNITION: 'Face Recognition',
  IMAGE_ANALYSIS: 'Image Analysis',
  REMOVE_BACKGROUND: 'Remove Background',
  PRINT_EXPORT: 'Print Export',
  AI_ASSISTANT: 'AI Assistant',
  EMOTION_CLASSIFICATION: 'Emotion Classification',
  SCENE_CLASSIFICATION: 'Scene Classification',
  POSE_CLASSIFICATION: 'Pose Classification',
};

const formatAddonName = (key: string, fallback?: string) => {
  return ADDON_DISPLAY_NAMES[key] || fallback || key.replace(/_/g, ' ');
};

const isJsonValid = (s: string) => {
  try {
    JSON.parse(s);
    return true;
  } catch {
    return false;
  }
};

export type AddonSettingsModalProps = {
  addonKey: string;
  addonTitle?: string;
  initialEventHooks?: any;
  onSave: (eventHooks: any) => Promise<void> | void;
};

export const AddonSettingsModal: React.FC<AddonSettingsModalProps> = ({
  addonKey,
  addonTitle,
  initialEventHooks,
  onSave,
}) => {
  const { closeModal } = useModal();
  const { showAlert } = useAlerts();

  const initial = initialEventHooks ? JSON.stringify(initialEventHooks, null, 2) : '{}';
  const [hookValue, setHookValue] = useState<string>(initial);
  const [saving, setSaving] = useState(false);

  const displayName = formatAddonName(addonKey, addonTitle);

  const hasChanges = (() => {
    if (!isJsonValid(hookValue) || !isJsonValid(initial)) return false;
    return JSON.stringify(JSON.parse(hookValue)) !== JSON.stringify(JSON.parse(initial));
  })();

  const handleSave = async () => {
    if (!isJsonValid(hookValue)) {
      showAlert('Settings could not be saved. Please review and try again.');
      return;
    }
    try {
      setSaving(true);
      await onSave(JSON.parse(hookValue));
      closeModal();
    } catch (e) {
      // The caller is expected to surface alerts; close handled there if needed.
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseModal containerStyle={{ width: 'min(720px, 92vw)', maxHeight: '90vh', overflowY: 'auto' }}>
      <div className={styles.header}>
        <h3 className={styles.title}>{displayName} Addon Settings</h3>
        <p className={styles.description}>
          Define conditions that determine when this addon runs automatically. Use schema criteria to limit which assets
          trigger the addon based on their metadata values.
        </p>
      </div>

      <div className={styles.body}>
        <AddonEventHooksForm value={hookValue} onChange={setHookValue} addonKey={addonKey} />
      </div>

      <div className={styles.actions}>
        <Button mode="tertiary" size="small" onClick={() => closeModal()} disabled={saving}>
          Cancel
        </Button>
        <Button mode="primary" size="small" onClick={handleSave} disabled={!hasChanges || saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </BaseModal>
  );
};

export default AddonSettingsModal;
